import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "@/i18n";
import { Logo } from "./Logo";

const links = [
  { to: "/la-casa", key: "casa" },
  { to: "/camere", key: "camere" },
  { to: "/gallery", key: "gallery" },
  { to: "/dove-siamo", key: "dove" },
  { to: "/cosa-vedere", key: "cosa" },
  { to: "/esperienze", key: "esperienze" },
  { to: "/recensioni", key: "recensioni" },
  { to: "/contatti", key: "contatti" },
];

const LangSwitch = ({ dark = false }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex items-center rounded-full border p-0.5 text-xs font-bold ${dark ? "border-sand/30" : "border-border"}`} data-testid="lang-switcher">
      {["it", "en", "fr", "de", "es"].map((l) => (
        <button
          key={l}
          data-testid={`lang-switch-${l}`}
          onClick={() => setLang(l)}
          className={`rounded-full px-2.5 py-1 uppercase transition-colors duration-200 ${
            lang === l ? "bg-terracotta text-sand" : dark ? "text-sand/70 hover:text-sand" : "text-ink-muted hover:text-ink"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export const Header = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-sand/85 backdrop-blur-md border-b border-amber-900/10 shadow-sm" : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <Link to="/" aria-label="Casa Vacanze Margherita — Home">
            <Logo light={!scrolled} />
          </Link>
          <nav className="hidden items-center gap-6 xl:flex" aria-label="Navigazione principale">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.key}`}
                className={({ isActive }) =>
                  `text-sm font-semibold transition-colors duration-200 ${
                    scrolled
                      ? isActive ? "text-terracotta" : "text-ink hover:text-terracotta"
                      : `${isActive ? "text-terracotta" : "text-ink"} bg-sand/60 backdrop-blur-sm rounded-full px-3 py-1 hover:text-terracotta`
                  }`
                }
              >
                {t.nav[l.key]}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <div className={`hidden sm:block ${scrolled ? "" : "rounded-full bg-sand/70 backdrop-blur-sm"}`}>
              <LangSwitch />
            </div>
            <Link
              to="/prenota"
              data-testid="nav-booking-cta"
              className="hidden rounded-full bg-terracotta px-5 py-2.5 text-sm font-bold text-sand shadow-lg shadow-terracotta/25 transition-all duration-200 hover:bg-terracotta-dark hover:shadow-xl hover:-translate-y-0.5 sm:block"
            >
              {t.nav.prenota}
            </Link>
            <button
              data-testid="mobile-menu-open"
              onClick={() => setOpen(true)}
              aria-label="Apri menu"
              className={`rounded-full p-2 xl:hidden ${scrolled ? "text-ink" : "text-ink bg-sand/70 backdrop-blur-sm"}`}
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-mare text-sand"
          >
            <div className="flex items-center justify-between px-4 py-3 sm:px-6">
              <Logo light />
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} aria-label="Chiudi menu" className="rounded-full p-2 text-sand">
                <X size={28} />
              </button>
            </div>
            <nav className="mt-6 flex flex-col gap-1 px-8" aria-label="Menu mobile">
              {[{ to: "/", key: "home" }, ...links, { to: "/prenota", key: "prenota" }].map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.05, duration: 0.4 }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-${l.key}`}
                    className={({ isActive }) =>
                      `block border-b border-sand/10 py-4 font-serif text-3xl font-medium transition-colors ${
                        isActive ? "text-ochre" : "text-sand hover:text-ochre"
                      }`
                    }
                  >
                    {t.nav[l.key]}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <div className="mt-8 px-8">
              <LangSwitch dark />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
